import { ProviderAdjustmentRemarkReportItem } from '../../api';
import { AdjustmentCodes, RemarkCodes } from '../dictionary';
import { formatCodeComposite, IDenialCode, parseDenialCode } from './denial-code';
import { UNKNOWN_REASON } from './denial-rows';
import { isRoutineAdjustment } from './routine-adjustments';

/**
 * Turns `/provider/report/adjustment-remark` into the rows the denials table shows.
 *
 * This replaces the shape built from `/provider/report/adjustment`, which returned only
 * `{group, reasonCode, count, amount}`. Everything below was measured against production on
 * 2026-09-30 (189 rows for a 7-day window), because the endpoint is not in the published spec and
 * its field names do not carry its semantics.
 *
 * ## The remark set belongs to the LINE, not to the adjustment
 *
 * John Russell, 2026-09-29: *"Remark codes describe the service line and aren't mapped one-to-one to
 * individual adjustments."* The data agrees loudly — of 189 rows, **61 carried more than one remark
 * code and 45 carried none**, and `CO:11` alone arrived three times: once bare, once with `M64`,
 * once with `N1, N657`.
 *
 * So one adjustment code fans out into several rows, and a single `CO:197.N54` composite — which is
 * what the design draws — asserts that N54 explains CO:197. Nothing in the data says that. A biller
 * who appeals CO:197 citing N54 may be citing a remark that was never attached to it, which is the
 * kind of wrong that survives review because it looks precise.
 *
 * `codes` therefore holds the CARC half alone (`CO:197`) and the remark set is a separate field.
 * They are rendered adjacent, never fused.
 *
 * ## `count` is adjustments
 *
 * Service-line adjustments — not claims, not remits. The column is labelled "Adjustments" for
 * exactly that reason: this feature's history is mislabelled counts, and `#522` is still open with
 * `count` documented as adjustments while the older report returned remits. A claim count is not
 * available here at all; the report carries no claim id.
 *
 * ## `amount` is signed
 *
 * Reversals and takebacks arrive negative — `CO:11` appeared at −640.20 twice and +640.20 once.
 * Rows keep the sign. Nothing here sums them: netting a takeback against a denial produces a figure
 * that is true of neither and hides the reversal completely.
 *
 * ## This is every adjustment, not only denials
 *
 * `CO:104`, `CO:107` and `CO:136` are contractual write-offs. The whole adjustment surface for the
 * week measured 189 rows and $581,429.49. Denials are a subset, separated by `isRoutineAdjustment`
 * the same way as before.
 *
 * ## No payer
 *
 * The report has no payer dimension, so no row can name one. The page renders the PAYER column
 * disabled with a tooltip rather than dropping it (FE-116), so enabling it later is a switch.
 */
export interface IDenialRemarkRow {
    /** Distinguishes rows that share a code but not a remark set. `DENIAL:CO:197|N1,N657`. */
    key: string;
    /** The parsed CARC half — what a worker rule for this group is built from. */
    code: IDenialCode;
    /** `CO:197`. The remark half is deliberately NOT folded in; see the note above. */
    codes: string;
    /** The distinct remark codes on the line, as returned. May be empty. */
    remarkCodes: string[];
    /** `N1, N657`, or an em dash when the line carried none. For display only. */
    remarkLabel: string;
    /** The official CARC text, or a marked fallback when the code is not in the dictionary. */
    reasonDescription: string;
    /** The official RARC text for each remark, joined. Empty when there are no remarks. */
    remarkDescription: string;
    /** Service-line adjustments. NOT claims, NOT remits, and not additive across rows. */
    adjustments: number;
    /** Signed. Negative on reversals and takebacks. */
    amount: number;
    /** A routine adjustment on a paid claim (CO-45, PR-1/2/3) rather than a denial. Hidden by default. */
    routine: boolean;
}

export interface IDenialRemarkRows {
    rows: IDenialRemarkRow[];
    /**
     * Report items that could not be read as a denial code.
     *
     * Returned rather than silently dropped: a table that quietly discards rows shows a total that
     * does not match the practice's adjustments and gives no hint why (NS-3).
     */
    skipped: number;
}

/** Shown in the remark column when the service line carried no remark at all. */
export const NO_REMARK_LABEL = '—';

const amountOf = (value: ProviderAdjustmentRemarkReportItem['amount']): number => {
    const raw = value && (value as { amount?: string }).amount;
    const parsed = Number(raw);

    return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * The remark set, ordered, so two rows carrying the same codes in a different order produce the same
 * key and the same label. The API's order is not documented and is not relied on.
 */
const normaliseRemarks = (codes: string[] | undefined): string[] =>
    [...new Set((codes || []).map(code => code.trim().toUpperCase()).filter(Boolean))].sort();

export const buildDenialRemarkRows = (
    items: ProviderAdjustmentRemarkReportItem[] | undefined,
): IDenialRemarkRows => {
    let skipped = 0;
    const rows: IDenialRemarkRow[] = [];

    (items || []).forEach(item => {
        const code = parseDenialCode(item.adjustmentGroup, item.adjustmentCode);
        if (!code) {
            skipped += 1;
            return;
        }

        const remarkCodes = normaliseRemarks(item.remarkCodes);
        const codes = formatCodeComposite(code);

        rows.push({
            key: `${code.kind}:${codes}|${remarkCodes.join(',')}`,
            code,
            codes,
            remarkCodes,
            remarkLabel: remarkCodes.length ? remarkCodes.join(', ') : NO_REMARK_LABEL,
            reasonDescription:
                (AdjustmentCodes as Record<string, string>)[code.reasonCode] || UNKNOWN_REASON,
            remarkDescription: remarkCodes
                .map(remark => (RemarkCodes as Record<string, string>)[remark])
                .filter(Boolean)
                .join(' '),
            adjustments: item.count || 0,
            amount: amountOf(item.amount),
            routine: isRoutineAdjustment(code),
        });
    });

    // Biggest money first, which is what the design sorts by and what the report can now answer.
    // Reversals are negative, so they sink to the bottom rather than being mixed in by magnitude —
    // a −$640 takeback is not the same kind of row as a $640 denial and should not sit beside it.
    rows.sort(
        (a, b) =>
            b.amount - a.amount ||
            b.adjustments - a.adjustments ||
            a.codes.localeCompare(b.codes) ||
            a.remarkLabel.localeCompare(b.remarkLabel),
    );

    return { rows, skipped };
};

/** The rows the table shows: routine adjustments only when asked for. */
export const visibleDenialRemarkRows = (
    rows: IDenialRemarkRow[],
    showRoutine: boolean,
): IDenialRemarkRow[] => (showRoutine ? rows : rows.filter(row => !row.routine));

/** A code with its separators and spaces removed: `co:197.n54` → `co197n54`. */
const compactCode = (code: string): string => code.replace(/[\s:.,-]/g, '');

/**
 * The rows matching a search by code, remark code, or official description, ignoring case. A blank
 * query matches every row.
 *
 * Codes match with or without their separator — `CO:16`, `CO-16` and `co16` all find `CO:16` —
 * because people paste codes from remits and payer letters in whichever form those used. The group
 * stays part of the match, so `CO-16` does not also find `PR:16`: the same reason code under another
 * group is a different adjustment.
 */
export const searchDenialRemarkRows = (
    rows: IDenialRemarkRow[],
    query: string,
): IDenialRemarkRow[] => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
        return rows;
    }
    const compactNeedle = compactCode(needle);

    return rows.filter(row => {
        if (row.reasonDescription.toLowerCase().includes(needle)) {
            return true;
        }
        if (row.remarkDescription.toLowerCase().includes(needle)) {
            return true;
        }
        if (!compactNeedle.length) {
            return false;
        }

        return (
            compactCode(row.codes.toLowerCase()).includes(compactNeedle) ||
            row.remarkCodes.some(remark => compactCode(remark.toLowerCase()).includes(compactNeedle))
        );
    });
};
